import type { Widget } from "../types/widget";

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

function hasNonBlank(map: Record<string, unknown>, ...keys: string[]): boolean {
  return keys.some((key) => {
    const value = map[key];
    return value != null && String(value).trim() !== "";
  });
}

/** Client-side checks aligned with backend V-output rules — blocks save before API call. */
export function validateWidgetDraftForSave(widget: Widget): string[] {
  const errors: string[] = [];
  const output = widget.output;
  if (!output?.format) {
    errors.push("output.format is required");
    return errors;
  }

  const mapping = output.mapping;
  if (!isRecord(mapping) || Object.keys(mapping).length === 0) {
    errors.push(
      "output.mapping is required and must not be empty — the agent must emit a complete mapping (not {})"
    );
    return errors;
  }

  const format = output.format.toLowerCase();
  if (format === "table") {
    if (mapping.chartType != null || mapping.optionTemplate != null) {
      errors.push("table format uses mapping.columns, not chartType/optionTemplate");
    }
    const columns = mapping.columns;
    if (!Array.isArray(columns) || columns.length === 0) {
      errors.push("output.mapping.columns is required for table widgets");
      return errors;
    }
    columns.forEach((col, index) => {
      if (!isRecord(col)) {
        errors.push(`output.mapping.columns[${index}] must be an object`);
        return;
      }
      if (!hasNonBlank(col, "header", "label", "title", "name")) {
        errors.push(`output.mapping.columns[${index}] missing header/label`);
      }
      const hasResultBinding = hasNonBlank(col, "path", "source", "field");
      const isComputedColumn =
        Object.prototype.hasOwnProperty.call(col, "formula") ||
        Object.prototype.hasOwnProperty.call(col, "constant");
      if (!hasResultBinding && !isComputedColumn) {
        errors.push(`output.mapping.columns[${index}] missing path/source/field`);
      }
    });
  } else if (format === "text" || format === "html") {
    if (mapping.template == null || String(mapping.template).trim() === "") {
      errors.push("output.mapping.template is required for text/html widgets");
    }
  } else if (format === "echarts") {
    const chartType = mapping.chartType != null ? String(mapping.chartType).trim().toLowerCase() : "";
    if (chartType === "table") {
      errors.push('table is not a chartType — use output.format "table" with mapping.columns');
    } else if (!chartType) {
      errors.push("output.mapping.chartType is required for echarts");
    }
    if (mapping.optionTemplate == null) {
      errors.push("output.mapping.optionTemplate is required for echarts");
    }
  }

  return errors;
}
